/**
 * Testimonials mosaic (sections/testimonials-mosaic.liquid)
 *
 * - Progress bar + prev/next arrows for every `.mosaic` on the page.
 * - Fits the quote line-clamp to the space actually left in each quote tile
 *   (tile height - padding - stars - author), recomputed on resize + font load.
 * - Truncated quote tiles become buttons that open the full review in a
 *   native <dialog> (one per section, rendered in Liquid).
 *
 * Loaded as a module, so it runs once per page even if the section is used
 * more than once. Init is idempotent; theme-editor re-renders are picked up
 * via `shopify:section:load`.
 */

const ROOT_SELECTOR = '.mosaic';
const QUOTE_TILE_SELECTOR = '.mosaic__tile--quote';
const EXPANDABLE_CLASS = 'is-expandable';
const FITTED_CLASS = 'is-fitted';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @param {Element | null} el */
const px = (el, prop) => (el ? parseFloat(getComputedStyle(el).getPropertyValue(prop)) || 0 : 0);

/** Height of an element including vertical margins (transform-independent). */
const outerHeight = (el) => (el ? el.offsetHeight + px(el, 'margin-top') + px(el, 'margin-bottom') : 0);

/** @param {HTMLElement} el */
const lineHeightOf = (el) => {
  const styles = getComputedStyle(el);
  const lh = styles.lineHeight;
  if (lh.endsWith('px')) return parseFloat(lh);
  const fontSize = parseFloat(styles.fontSize) || 16;
  const n = parseFloat(lh);
  return Number.isFinite(n) ? n * fontSize : fontSize * 1.4;
};

/**
 * Fit the quote clamp in one tile and flag it as expandable when truncated.
 * @param {HTMLElement} tile
 */
function fitTile(tile) {
  const quote = /** @type {HTMLElement | null} */ (tile.querySelector('.mosaic__quote'));
  const text = /** @type {HTMLElement | null} */ (tile.querySelector('.mosaic__text'));
  if (!quote || !text) return;

  // Reset to the CSS default (mobile: fallback clamp; desktop: no clamp)
  text.classList.remove(FITTED_CLASS);
  text.style.removeProperty('--mosaic-lines');

  // Hover-reveal tiles position the quote layer absolutely over the tile,
  // so the quote layer itself is the height-constrained box.
  const box = getComputedStyle(quote).position === 'absolute' ? quote : tile;
  const boxHeight = box.clientHeight - px(box, 'padding-top') - px(box, 'padding-bottom');
  if (boxHeight <= 0) return; // not rendered (display:none etc.)

  const stars = quote.querySelector('.mosaic__stars');
  const author = quote.querySelector('.mosaic__author');
  const available =
    boxHeight - outerHeight(stars) - outerHeight(author) - px(text, 'margin-top') - px(text, 'margin-bottom');

  const lineHeight = lineHeightOf(text);
  const lines = Math.max(1, Math.floor((available + 0.5) / lineHeight));
  // scrollHeight reports the full (unclamped) content height
  const truncated = text.scrollHeight > lines * lineHeight + 1;

  if (truncated) {
    text.style.setProperty('--mosaic-lines', String(lines));
    text.classList.add(FITTED_CLASS);
  } else {
    // Enough room for the whole quote: lift the mobile fallback clamp too.
    text.style.setProperty('--mosaic-lines', 'none');
  }

  setExpandable(tile, truncated);
}

/**
 * @param {HTMLElement} tile
 * @param {boolean} on
 */
function setExpandable(tile, on) {
  tile.classList.toggle(EXPANDABLE_CLASS, on);
  if (on) {
    tile.setAttribute('role', 'button');
    tile.setAttribute('tabindex', '0');
    tile.setAttribute('aria-haspopup', 'dialog');
  } else {
    tile.removeAttribute('role');
    tile.removeAttribute('tabindex');
    tile.removeAttribute('aria-haspopup');
  }
}

/** @param {HTMLElement} root */
function fitRoot(root) {
  root.querySelectorAll(QUOTE_TILE_SELECTOR).forEach((tile) => fitTile(/** @type {HTMLElement} */ (tile)));
}

/** @param {HTMLElement} root */
function updateProgress(root) {
  const viewport = root.querySelector('[data-mosaic-viewport]');
  const bar = /** @type {HTMLElement | null} */ (root.querySelector('[data-mosaic-progress] span'));
  if (!viewport || !bar) return;
  const max = viewport.scrollWidth - viewport.clientWidth;
  const p = max > 0 ? viewport.scrollLeft / max : 0;
  bar.style.transform = `scaleX(${p})`;
}

/**
 * @param {HTMLElement} root
 * @param {number} cols
 */
function scrollByCols(root, cols) {
  const viewport = root.querySelector('[data-mosaic-viewport]');
  const track = root.querySelector('[data-mosaic-track]');
  const tile = root.querySelector('.mosaic__tile');
  if (!viewport) return;
  // Measure rendered values: mobile overrides --tile-w / --gap with literals.
  const tileWidth = tile ? tile.getBoundingClientRect().width : px(root, '--tile-w') || 360;
  const gap = track ? px(track, 'column-gap') : 0;
  viewport.scrollBy({ left: cols * (tileWidth + gap), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
}

/* ---------- Full-review dialog ---------- */

/**
 * @param {HTMLElement} root
 * @param {HTMLElement} tile
 */
function openReview(root, tile) {
  const dialog = /** @type {HTMLDialogElement | null} */ (root.querySelector('[data-mosaic-dialog]'));
  if (!dialog || typeof dialog.showModal !== 'function' || dialog.open) return;

  const srcStars = tile.querySelector('.mosaic__stars');
  const srcText = tile.querySelector('.mosaic__text');
  const srcAuthor = tile.querySelector('.mosaic__author');
  const stars = dialog.querySelector('[data-mosaic-dialog-stars]');
  const text = dialog.querySelector('[data-mosaic-dialog-text]');
  const author = dialog.querySelector('[data-mosaic-dialog-author]');

  if (stars) {
    stars.innerHTML = srcStars ? srcStars.innerHTML : '';
    const label = srcStars?.getAttribute('aria-label');
    if (label) stars.setAttribute('aria-label', label);
    else stars.removeAttribute('aria-label');
  }
  if (text) text.textContent = srcText ? srcText.textContent : '';
  if (author) author.textContent = srcAuthor ? srcAuthor.textContent : '';

  dialog._mosaicTrigger = tile;
  dialog.showModal();

  if (!document.documentElement.hasAttribute('scroll-lock')) {
    document.documentElement.setAttribute('scroll-lock', '');
    dialog._mosaicLocked = true;
  }
}

/** @param {HTMLDialogElement} dialog */
function bindDialog(dialog) {
  if (dialog.dataset.mosaicBound) return;
  dialog.dataset.mosaicBound = 'true';

  dialog.addEventListener('click', (event) => {
    // Backdrop clicks target the <dialog> itself; content sits in an inner wrapper.
    if (event.target === dialog || event.target.closest('[data-mosaic-dialog-close]')) dialog.close();
  });

  dialog.addEventListener('close', () => {
    if (dialog._mosaicLocked) {
      document.documentElement.removeAttribute('scroll-lock');
      dialog._mosaicLocked = false;
    }
    const trigger = dialog._mosaicTrigger;
    dialog._mosaicTrigger = null;
    if (trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
  });
}

/* ---------- Init ---------- */

/** @param {HTMLElement} root */
function initRoot(root) {
  if (root.dataset.mosaicInit) {
    fitRoot(root);
    return;
  }
  root.dataset.mosaicInit = 'true';

  const viewport = root.querySelector('[data-mosaic-viewport]');
  if (viewport) viewport.addEventListener('scroll', () => updateProgress(root), { passive: true });

  root.querySelector('[data-mosaic-prev]')?.addEventListener('click', () => scrollByCols(root, -1));
  root.querySelector('[data-mosaic-next]')?.addEventListener('click', () => scrollByCols(root, 1));

  const dialog = root.querySelector('[data-mosaic-dialog]');
  if (dialog) bindDialog(/** @type {HTMLDialogElement} */ (dialog));

  root.addEventListener('click', (event) => {
    const tile = event.target.closest(`.mosaic__tile.${EXPANDABLE_CLASS}`);
    if (!tile || !root.contains(tile)) return;
    openReview(root, tile);
  });

  root.addEventListener('keydown', (event) => {
    const tile = event.target;
    if (!(tile instanceof HTMLElement) || !tile.classList.contains(EXPANDABLE_CLASS)) return;
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      openReview(root, tile);
    }
  });

  updateProgress(root);
  fitRoot(root);
}

const allRoots = () => /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll(ROOT_SELECTOR));

function initAll() {
  allRoots().forEach(initRoot);
}

let resizeTimer = 0;
let lastWidth = window.innerWidth;
window.addEventListener(
  'resize',
  () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      allRoots().forEach(updateProgress);
      // Tile sizes only depend on width; skip mobile URL-bar height changes.
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      allRoots().forEach(fitRoot);
    }, 150);
  },
  { passive: true }
);

if (document.fonts && document.fonts.ready) {
  document.fonts.ready.then(() => allRoots().forEach(fitRoot));
}

document.addEventListener('shopify:section:load', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  target.querySelectorAll(ROOT_SELECTOR).forEach((root) => initRoot(/** @type {HTMLElement} */ (root)));
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll, { once: true });
} else {
  initAll();
}
